import maplibregl from 'maplibre-gl';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import 'maplibre-gl/dist/maplibre-gl.css';

import { Button } from '@/components/ui';

const MAPTILER_KEY = (import.meta.env.VITE_MAPTILER_API_KEY ?? '').trim();
const KEY_LOOKS_VALID = /^[A-Za-z0-9_-]{8,}$/.test(MAPTILER_KEY);
const STYLE_URL = KEY_LOOKS_VALID
  ? `https://api.maptiler.com/maps/streets-v2/style.json?key=${MAPTILER_KEY}`
  : 'https://demotiles.maplibre.org/style.json';

interface GISMapEditorProps {
  initialGeometry: GeoJSON.Geometry | null;
  onSubmit: (geom: GeoJSON.Geometry) => void;
  pending: boolean;
}

/**
 * Click-to-pin point editor. Click sets the pin, right-click clears it.
 * If `initialGeometry` is a Point, the pin starts pre-placed and the map
 * recenters on it.
 */
export function GISMapEditor({ initialGeometry, onSubmit, pending }: GISMapEditorProps) {
  const { t } = useTranslation('studio');
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markerRef = useRef<maplibregl.Marker | null>(null);

  const initialPin =
    initialGeometry?.type === 'Point'
      ? (initialGeometry.coordinates as [number, number])
      : null;
  const [pin, setPin] = useState<[number, number] | null>(initialPin);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: STYLE_URL,
      center: initialPin ?? [10, 50],
      zoom: initialPin ? 6 : 3,
      attributionControl: { compact: true },
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'bottom-right');
    map.on('click', (e) => {
      const next: [number, number] = [e.lngLat.lng, e.lngLat.lat];
      setPin(next);
    });
    map.on('contextmenu', () => setPin(null));
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync the marker with `pin`
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (markerRef.current) {
      markerRef.current.remove();
      markerRef.current = null;
    }
    if (pin) {
      markerRef.current = new maplibregl.Marker({ color: '#0ea5a4' })
        .setLngLat(pin)
        .addTo(map);
    }
  }, [pin]);

  return (
    <div className="space-y-3">
      <p className="text-small text-slate-600">{t('gis.editor.pin.hint')}</p>
      <div ref={containerRef} className="h-80 w-full overflow-hidden rounded border border-slate-200" />
      <div className="flex items-center justify-between">
        <p className="font-mono text-small text-slate-500">
          {pin ? `${pin[1].toFixed(5)}, ${pin[0].toFixed(5)}` : t('gis.editor.pin.noPin')}
        </p>
        <Button
          onClick={() => pin && onSubmit({ type: 'Point', coordinates: pin })}
          disabled={!pin || pending}
        >
          {t('gis.editor.save')}
        </Button>
      </div>
    </div>
  );
}
